import { useCallback, useContext } from 'react'

import { ShowWarningAction, WarningContext } from 'nexchange-service/module/common'
import { requestGetPairRates } from 'nexchange-service/module/coins'

import { useOnGetPairErrorHandler, useOnGetPairSuccessHandler } from './handleResponse'
import { useCoinInputLoadingSetter } from './useCoinInputLoadingSetter'
import { AvailableCurrenciesContext, SelectedCoinsContext, TCoinInputDto } from '../stateManagement'
import { validateAmount } from '../utils/amountLimitValidationHandling'
import { ECoinInputType } from '../constants'

const getSwitchedPair = ({
    fromDto,
    toDto,
    fromPairs,
}: {
    fromDto: TCoinInputDto
    toDto: TCoinInputDto
    fromPairs: object
}): {
    fromAmount: number
    fromCode: string
    toCode: string
    amountOf: ECoinInputType
} => {
    const { code: newFromCoinCode } = toDto
    const { code: newToCoinCode } = fromDto

    const isReversedPairIsValid = fromPairs[newFromCoinCode]?.includes(newToCoinCode)
    if (!isReversedPairIsValid) {
        console.error('No valid pairs')
    }

    // When switching, we need to validate against the new pair's limits
    const { validatedAmount } = validateAmount({
        coinCode: newFromCoinCode,
        amount: toDto.amount, // Previous "to" amount becomes "from" amount
        minAmount: toDto.minAmount,
        maxAmount: toDto.maxAmount,
    })

    return {
        fromAmount: validatedAmount,
        fromCode: newFromCoinCode,
        toCode: newToCoinCode,
        amountOf: ECoinInputType.FROM, // Always use FROM after validation
    }
}

const useCoinSwitchHandler = () => {
    const { dispatchAction: dispatchWarningAction } = useContext(WarningContext)
    const {
        state: {
            availablePairs: { fromPairs },
        },
    } = useContext(AvailableCurrenciesContext)
    const {
        state: { from, to },
    } = useContext(SelectedCoinsContext)

    const setCoinInputsLoading = useCoinInputLoadingSetter()
    const onGetPairSuccess = useOnGetPairSuccessHandler()
    const onGetPairError = useOnGetPairErrorHandler()

    return useCallback(async () => {
        setCoinInputsLoading(true)

        try {
            const { fromCode, toCode, fromAmount, amountOf } = getSwitchedPair({
                fromDto: from,
                toDto: to,
                fromPairs,
            })

            const { data: apiPairDto, error } = await requestGetPairRates({
                fromCode,
                toCode,
                amount: fromAmount,
                amountOf,
            })

            if (apiPairDto) {
                onGetPairSuccess(apiPairDto)
            } else if (error?.response?.data) {
                const { max_amount_quote, min_amount_quote } = error.response.data

                if (max_amount_quote || min_amount_quote) {
                    // Retry with validated amount from error response
                    const { validatedAmount } = validateAmount({
                        coinCode: fromCode,
                        amount: fromAmount,
                        minAmount: Number(min_amount_quote || 0),
                        maxAmount: Number(max_amount_quote || Infinity),
                    })

                    const { data: retryApiPairDto, error: retryError } = await requestGetPairRates({
                        fromCode,
                        toCode,
                        amount: validatedAmount,
                        amountOf: ECoinInputType.FROM,
                    })

                    if (retryApiPairDto) {
                        onGetPairSuccess(retryApiPairDto)
                    } else {
                        console.log('Retry error:', retryError)
                        onGetPairError({
                            errorDto: retryError,
                            dataLayerEvent: 'fail_switch_pair_retry',
                        })
                    }
                } else {
                    onGetPairError({
                        errorDto: error,
                        dataLayerEvent: 'fail_switch_pair_no_limits',
                    })
                }
            } else {
                onGetPairError({
                    errorDto: error,
                    dataLayerEvent: 'fail_switch_pair_no_response',
                })
            }
        } catch (ex) {
            dispatchWarningAction(
                new ShowWarningAction(
                    'Something went wrong. Please try again or contact our support team.',
                ),
            )
        } finally {
            setCoinInputsLoading(false)
        }
    }, [
        from,
        to,
        fromPairs,
        setCoinInputsLoading,
        onGetPairSuccess,
        onGetPairError,
        dispatchWarningAction,
    ])
}

export { useCoinSwitchHandler }
